import React, { useState, FC, DetailedHTMLProps, HTMLAttributes } from 'react';

import clsx from 'clsx';
import {
  languages,
  AvailableLanguages,
  ExtendedDrupalNode,
  sendBeaconEvent,
  sendGoogleEvent,
  BeaconEventMarketingPageElementLocation,
  BeaconEventMarketingPageElementType,
  formatShortLangPrefix
} from '@lucid/utils';

import Link from '../link/link';
import Dropdown, {
  DropdownReactSelectProps,
  DropdownReactSelectOptionProps,
  DefaultDropdownOptionType
} from '../form/dropdown/dropdown';

export interface LanguageSwitcherProps extends DropdownReactSelectProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Hides the languages in the select option. */
  disabledLanguages?: AvailableLanguages[];
  /** The entity values to allow for customization of urls. */
  entityTranslations: ExtendedDrupalNode[];
  /** The short language prefix to create proper urls. */
  shortLangPrefix: string | AvailableLanguages;
}

interface SelectCustomOptionsProps extends DropdownReactSelectOptionProps {
  data: any;
  /** Checks if the dropdown element is selected. */
  isSelected: boolean;
}

interface LanguageSwitchOptions extends DefaultDropdownOptionType {
  /** The english name for each language. */
  name: string;
  /** The language prefix. */
  langPrefix: string;
}

type SelectCustomOptionInnerRef = (instance: HTMLDivElement | HTMLAnchorElement | null) => void;
type SelectCustomOptionInnerProps = DetailedHTMLProps<HTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>;

const SelectCustomOption = ({ cx, data, innerRef, innerProps, isSelected, theme, type, ...rest }: SelectCustomOptionsProps) => (
  <Link
    {...rest}
    {...innerProps as unknown as SelectCustomOptionInnerProps}
    ref={innerRef as unknown as SelectCustomOptionInnerRef}
    className={
      clsx(
        'reactSelectDropdown__link',
        isSelected && 'reactSelectDropdown__link--selected'
      )
    }
    to={data.value}
    variant="subNavLink"
  >
    {data.label}
  </Link>
);

const LanguageSwitcher: FC<LanguageSwitcherProps> = ({
  disabledLanguages,
  entityTranslations,
  shortLangPrefix,
  ...rest
}) => {
  const currentShortLangPrefix = shortLangPrefix as AvailableLanguages;
  const shouldShowLangSelect = !disabledLanguages?.includes(currentShortLangPrefix);
  const switcherValues: LanguageSwitchOptions[] = Object.keys(languages)
    .filter(lang => {
      const currentLang = formatShortLangPrefix(lang) as AvailableLanguages;
      return !disabledLanguages?.includes(currentLang);
    })
    .reduce<LanguageSwitchOptions[]>(
    (previous, langPrefix) => {
      const { shortLangPrefix: sLangPrefix } = languages[langPrefix] as { shortLangPrefix: AvailableLanguages };

      const { entityUrl } = (entityTranslations || []).find(
        ({ status, entityLanguage }) => status && entityLanguage!.id.includes(sLangPrefix)
      ) || {};

      if (entityUrl) {
        // check for drupal /homepage alias and remove it in the language switcher
        if (entityUrl.path === '') {
          entityUrl.path = '/';
        }

        if (entityUrl.path === `/${sLangPrefix}/`) {
          entityUrl.path = `/${sLangPrefix}`;
        }
      }

      return [
        ...previous,
        {
          value: entityUrl?.path
            ? entityUrl.path
            : `/${sLangPrefix === 'en' ? '' : sLangPrefix}`,
          label: languages[langPrefix].name,
          name: languages[langPrefix].englishName,
          langPrefix
        }
      ];
    }, []);
  const [defaultValue] = switcherValues.filter(option => option.langPrefix === currentShortLangPrefix);
  const [currentLanguage, setCurrentLanguage] = useState(defaultValue?.name || 'English');

  return shouldShowLangSelect ? (
    <Dropdown
      {...rest}
      blurInputOnSelect
      closeMenuOnSelect
      menuIsOpen
      aria-label="Site Language Picker"
      components={{
        Option: SelectCustomOption
      }}
      defaultValue={defaultValue}
      instanceId="languageSwitcher"
      isSearchable={false}
      onChange={(newValue) => {
        const currentValue = newValue as unknown as LanguageSwitchOptions;
        setCurrentLanguage(currentValue.name);
        sendGoogleEvent({
          name: 'changedPageLanguage',
          data: {
            langBefore: currentLanguage,
            langAfter: currentValue.name
          }
        });
      }}
      onFocus={() => {
        sendBeaconEvent({
          name: 'clickedMarketingPageElement',
          elementLocation: BeaconEventMarketingPageElementLocation.footer,
          elementDetail: 'lang-toggle',
          elementType: BeaconEventMarketingPageElementType.link
        });
      }}
      options={switcherValues}
      variant="languageSwitcher"
    />
  ) : null;
};

export { LanguageSwitcher };
export default LanguageSwitcher;
